import { FC, useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { ITimeRangeParams } from '../../../../../apis/dashboard/dashboard.interfaces';
import dayjs, { Dayjs } from 'dayjs';
import ControlledDateRangePicker from '../../../../components/controlledComponents/datePicker/datePicker.component';

export interface TimeRangePickerProps {
  value?: ITimeRangeParams;
  onChange: (value: ITimeRangeParams) => void;
  dataTestId?: string;
  showCustomRange?: boolean;
}

export const TimeRangePicker: FC<TimeRangePickerProps> = ({
  value,
  onChange,
  dataTestId = 'time-range-picker',
  showCustomRange = true,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const [period, setPeriod] = useState<ITimeRangeParams['period']>(
    value?.period || 'monthly'
  );
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const getDefaultDateRange = (periodType: ITimeRangeParams['period']): ITimeRangeParams => {
    const today = dayjs();
    let startDate: Dayjs;
    let endDate: Dayjs = today;

    switch (periodType) {
      case 'daily':
        startDate = today;
        break;
      case 'weekly':
        startDate = today.startOf('week');
        break;
      case 'monthly':
        startDate = today.startOf('month');
        break;
      case 'quarterly':
        const quarter = Math.floor(today.month() / 3);
        startDate = today.month(quarter * 3).startOf('month');
        break;
      case 'yearly':
        startDate = today.startOf('year');
        break;
      default:
        startDate = today.startOf('month');
    }

    return {
      period: periodType,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const handlePeriodChange = (event: SelectChangeEvent<string>) => {
    const newPeriod = event.target.value as ITimeRangeParams['period'];
    setPeriod(newPeriod);

    if (newPeriod === 'custom') {
      // Keep current custom range if available
      if (customRange[0] && customRange[1]) {
        onChange({
          period: 'custom',
          startDate: customRange[0].toISOString(),
          endDate: customRange[1].toISOString(),
        });
      }
    } else {
      const range = getDefaultDateRange(newPeriod);
      onChange(range);
    }
  };

  const handleCustomRangeChange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
    const dates: [Dayjs | null, Dayjs | null] = [fromDate, toDate];
    setCustomRange(dates);
    if (fromDate && toDate) {
      onChange({
        period: 'custom',
        startDate: fromDate.toISOString(),
        endDate: toDate.toISOString(),
      });
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      flexWrap="wrap"
      data-testid={dataTestId}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <CalendarToday sx={{ color: theme.palette.primary.main }} />
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 } }}>
          <Select
            value={period || 'monthly'}
            onChange={handlePeriodChange}
            displayEmpty
            data-testid={`${dataTestId}-period-select`}
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: { xs: '44px', sm: '40px' },
            }}
          >
            <MenuItem value="daily">
              {translateText('dashboard.timeRange.daily')}
            </MenuItem>
            <MenuItem value="weekly">
              {translateText('dashboard.timeRange.weekly')}
            </MenuItem>
            <MenuItem value="monthly">
              {translateText('dashboard.timeRange.monthly')}
            </MenuItem>
            <MenuItem value="quarterly">
              {translateText('dashboard.timeRange.quarterly')}
            </MenuItem>
            <MenuItem value="yearly">
              {translateText('dashboard.timeRange.yearly')}
            </MenuItem>
            {showCustomRange && (
              <MenuItem value="custom">
                {translateText('dashboard.timeRange.custom')}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>

      {period === 'custom' && showCustomRange && (
        <Box sx={{ minWidth: { xs: '100%', sm: '280px' } }}>
          <ControlledDateRangePicker
            id={`${dataTestId}-custom-range`}
            name="customRange"
            placeholder={translateText('dashboard.timeRange.selectRange')}
            value={customRange}
            onChange={handleCustomRangeChange}
            isGridItem={false}
            isClearable
            dataTestId={`${dataTestId}-custom-range-picker`}
          />
        </Box>
      )}
    </Box>
  );
};
